import type { ReactElement } from 'react';
import { PageShell } from '../Page';
import type { PageProps } from '../types';
import { EditableText } from '../../components/Editable';
import type { SelectionField } from '../../state/useSelectionContext';
import { Frame, HeadRow } from './primitives';

/**
 * stats (brief §4 `04 stats`, design.md V15 "opportunity stats", optional):
 * FULL light, 3-4 tiles. `content.fields` keys are exactly what assembly
 * seeds (design.md V15 "Seeds: … stats `content.fields` = {tagline, source,
 * stat1Label, stat1Value, stat1Note … stat4Label, stat4Value, stat4Note}").
 * A tile whose Value is empty is skipped in print/share/readOnly (never a
 * fabricated number); the workspace always shows all four as editable
 * placeholders so authoring the first one doesn't require guessing a hidden
 * slot exists.
 */
export function StatsPage({ proposal, section, actions, readOnly }: PageProps): ReactElement {
  const fields = section.content.fields ?? {};
  const setField = (key: string, value: string) => {
    actions.saveContent(section, { fields: { ...fields, [key]: value } });
  };
  const headline = section.content.headline || 'The opportunity';
  const slots = [1, 2, 3, 4].map((n) => ({
    n,
    labelKey: `stat${n}Label`,
    valueKey: `stat${n}Value`,
    noteKey: `stat${n}Note`,
  }));
  const visible = readOnly ? slots.filter((slot) => (fields[slot.valueKey] ?? '').trim() !== '') : slots;

  return (
    <PageShell
      section={section}
      readOnly={readOnly}
      onTitleSave={(title) => actions.saveTitle(section, title)}
      className="mv3-stats-root"
      hideTitle
    >
      <Frame surface="light" proposal={proposal} section={section}>
        <div className="mv3-full-pane">
          <HeadRow
            title={
              <EditableText
                value={headline}
                field="headline"
                placeholder="The opportunity"
                readOnly={readOnly}
                clampLines={1}
                onSave={(next) => actions.saveContent(section, { headline: next })}
              />
            }
            tagline={
              <EditableText
                value={fields.tagline}
                field="fields.tagline"
                placeholder="Numbers that make the goal feel reachable."
                readOnly={readOnly}
                clampLines={2}
                onSave={(next) => setField('tagline', next)}
              />
            }
          />
          <div className="mv3-stats-grid">
            {visible.map((slot) => (
              <div className="mv3-stat-tile" key={slot.n}>
                <div className="mv3-stat-tile-label">
                  <EditableText
                    value={fields[slot.labelKey]}
                    field={`fields.${slot.labelKey}` as SelectionField}
                    placeholder="Label"
                    readOnly={readOnly}
                    clampLines={1}
                    onSave={(next) => setField(slot.labelKey, next)}
                  />
                </div>
                <div className="mv3-stat-tile-value">
                  <EditableText
                    value={fields[slot.valueKey]}
                    field={`fields.${slot.valueKey}` as SelectionField}
                    placeholder="—"
                    readOnly={readOnly}
                    clampLines={1}
                    onSave={(next) => setField(slot.valueKey, next)}
                  />
                </div>
                <div className="mv3-stat-tile-note">
                  <EditableText
                    value={fields[slot.noteKey]}
                    field={`fields.${slot.noteKey}` as SelectionField}
                    placeholder="One line of context"
                    readOnly={readOnly}
                    clampLines={2}
                    onSave={(next) => setField(slot.noteKey, next)}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mv3-stats-source">
            <EditableText
              value={fields.source}
              field="fields.source"
              placeholder="Source line"
              readOnly={readOnly}
              clampLines={1}
              onSave={(next) => setField('source', next)}
            />
          </div>
        </div>
      </Frame>
    </PageShell>
  );
}
